import { useEffect } from 'react';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { useDebounced } from 'v2/hooks/useDebounced';
import datasetIcon from 'v2/icons/datasetAvatar.svg';
import {
  Contact,
  DataProduct,
  DataVendor,
  DrmEntityBackend,
  LinkEntityInfoType,
  LinkEntityType,
} from 'v2/models';
import {
  getContactAvatarURL,
  getDatasetAvatarURL,
  getDataVendorAvatarURL,
} from 'v2/utils';

export default function useGetDRMEntityInfo(
  drmEntity: LinkEntityType | undefined
) {
  const query = useQuery<LinkEntityInfoType | undefined>(
    ['useGetDRMEntityInfo'],
    async () => {
      if (!drmEntity?.entity) {
        return;
      }
      const drmEntityInfo = await restFactory.get<
        DataVendor | Contact | DataProduct
      >(`/dj/api/v1/${drmEntity.entity}/${drmEntity.id}`);

      if (drmEntityInfo) {
        return getDRMEntity(drmEntity, drmEntityInfo);
      }

      return;
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: Boolean(drmEntity),
      keepPreviousData: false,
    }
  );

  const { debounced: debounceSearch, cancel } = useDebounced(() =>
    query.refetch()
  );

  useEffect(() => {
    if (drmEntity) {
      debounceSearch();
    } else {
      cancel();
      query.remove();
    }
  }, [drmEntity]);

  return query;
}

function getDRMEntity(
  drmEntity: LinkEntityType,
  drmEntityInfo: DataVendor | Contact | DataProduct
): LinkEntityInfoType {
  const drmEntityDetails: LinkEntityInfoType = {
    ...drmEntity,
  };

  switch (drmEntityDetails.entity) {
    case DrmEntityBackend.DATA_VENDOR: {
      const dataVendor = drmEntityInfo as DataVendor;
      setThroughDataVendor(dataVendor, drmEntityDetails);
      break;
    }
    case DrmEntityBackend.CONTACT: {
      const contact = drmEntityInfo as Contact;
      setThroughContact(contact, drmEntityDetails);
      break;
    }
    case DrmEntityBackend.DATASET: {
      const dataset = drmEntityInfo as DataProduct;
      setThroughDataset(dataset, drmEntityDetails);
      break;
    }
  }
  return drmEntityDetails;
}

function setThroughDataVendor(
  dataVendor: DataVendor,
  drmEntityDetails: LinkEntityInfoType
) {
  drmEntityDetails.avatar = getDataVendorAvatarURL(dataVendor);
  drmEntityDetails.name = dataVendor.name;
  drmEntityDetails.description = dataVendor.classification;
}

function setThroughContact(
  contact: Contact,
  drmEntityDetails: LinkEntityInfoType
) {
  drmEntityDetails.avatar = getContactAvatarURL(contact);
  drmEntityDetails.name = contact.name;
  const email = contact.primary_associations?.email?.email || '';
  const title = contact.title || '';
  drmEntityDetails.description = `${title} \n ${email}`;
}

function setThroughDataset(
  dataset: DataProduct,
  drmEntityDetails: LinkEntityInfoType
) {
  drmEntityDetails.avatar = datasetIcon;
  drmEntityDetails.name = dataset.name;
  if (dataset.data_vendor?.id !== null) {
    drmEntityDetails.description = dataset.data_vendor?.name || '';
    drmEntityDetails.avatar = getDatasetAvatarURL(dataset);
  }
}
